import { bcCategorySuggest, filingCard, PRODUCT_BEHAVIOUR } from "./bc";
import { displayCopy, displayStatus, documentationUnlocked, statusLabel, typeLabel } from "./display";

import { partyLabel } from "./notifications";
import { formatWhen } from "./time";
import { Incident, IncidentAmendment, IncidentEvent } from "./types";

function esc(value: unknown): string {
  return String(value ?? "—")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function row(label: string, value: unknown, tag: string): string {
  return `<tr><th scope="row">${esc(label)}</th><td>${esc(value)}</td><td>${esc(tag)}</td></tr>`;
}

function eventLine(event: IncidentEvent): string {
  return `<li><time datetime="${esc(event.at)}">${esc(formatWhen(event.at))}</time> — ${esc(
    event.actor_name || event.actor_user_id
  )} — ${esc(event.type.replace(/_/g, " "))}${event.payload?.note ? `: ${esc(event.payload.note)}` : ""}</li>`;
}

function amendmentBlock(amendment: IncidentAmendment): string {
  const changes = amendment.changes
    .map(
      (change) =>
        `<p><strong>${esc(change.field)}</strong><br/>Original: ${esc(change.old)}<br/>Amended: ${esc(change.new)}</p>`
    )
    .join("");
  return `<article class="amendment"><h3>Amendment ${esc(formatWhen(amendment.at))}</h3><p>${esc(
    amendment.author_name
  )} — ${esc(amendment.reason)}</p>${changes}</article>`;
}

/** Printable record. Staff copy this into the official form. NeuraVue does not submit it. */
export function buildExportHtml(incident: Incident): string {
  const status = statusLabel(displayStatus(incident));
  const unlocked = documentationUnlocked(incident);
  const doc = incident.documentation;
  const waiting =
    !incident.confirmed_at && incident.status !== "false_alert_pending_confirm" && incident.status !== "closed";
  const title = waiting
    ? "Alert record — awaiting staff review"
    : incident.province === "QC"
      ? "PROJET / DRAFT — Déclaration et signalement"
      : incident.type === "fall"
        ? "DRAFT — Post-fall incident record (Ontario)"
        : "DRAFT — Incident record";

  const alertRows = [
    row("Record", incident.id, "Filled by NeuraVue"),
    row(
      "Home",
      `${incident.facility_name}${incident.home_licence ? ` (licence ${incident.home_licence})` : ""}`,
      "Filled by NeuraVue"
    ),
    row("Status", status, "Filled by NeuraVue"),
    row("Alert", incident.alert_id, "Filled by NeuraVue"),
    row("Type", typeLabel(incident.type), "Filled by NeuraVue"),
    row("Sub-type", incident.alert_subtype || "—", "Filled by NeuraVue"),
    row("Alert time", formatWhen(incident.alert_at), "Filled by NeuraVue"),
    row("Camera and place", `${incident.camera_id || "—"} — ${incident.location || "—"}`, "Filled by NeuraVue"),
    row("System note", displayCopy(incident.alert_summary), "Filled by NeuraVue"),
    row(
      "Staff notified",
      incident.notified_staff.map((person) => `${person.name}, ${person.role}, ${formatWhen(person.at)}`).join("; ") ||
        "—",
      "Filled by NeuraVue"
    ),
    row(
      "Opened by",
      incident.opened_by ? `${incident.opened_by} at ${formatWhen(incident.opened_at)}` : "—",
      "Filled by NeuraVue"
    ),
    row("With the resident at", formatWhen(incident.arrived_at), "Filled by NeuraVue"),
    row(
      "Confirmed by",
      incident.confirmed_at
        ? `${incident.confirmed_by_name || incident.confirmed_by} at ${formatWhen(incident.confirmed_at)}`
        : "Not confirmed",
      "Completed by staff"
    ),
    row(
      "Clip reference",
      incident.clip_ref ? `${incident.clip_ref.id} (${incident.clip_ref.retention} retention)` : "—",
      "Filled by NeuraVue"
    ),
    row("Model version", incident.model_version || "—", "Filled by NeuraVue")
  ].join("");

  const form = unlocked
    ? `
    <h2>1. What happened</h2>
    <table>${[
      row("Resident", doc.resident_name || incident.resident?.display_name, "Completed by staff"),
      row("Event time", formatWhen(doc.event_time), "Completed by staff"),
      row("Discovered", formatWhen(doc.discovered_at), "Completed by staff"),
      row("Location", doc.location, "Completed by staff"),
      row(
        "Witnessed",
        doc.witnessed ? `${doc.witnessed}${doc.witnessed_by ? ` — ${doc.witnessed_by}` : ""}` : "—",
        "Completed by staff"
      ),
      row("What I observed", doc.observed, "Completed by staff"),
      row("What led up to it", doc.leading_events, "Completed by staff"),
      row("Resident's words", doc.resident_statement, "Completed by staff"),
      row("Other residents", doc.other_residents, "Completed by staff"),
      row("People present", doc.persons_present, "Completed by staff")
    ].join("")}</table>
    <h2>2. Assessment</h2>
    <table>${[
      row("Consciousness", doc.consciousness, "Completed by staff"),
      row("Vital signs", doc.vitals, "Completed by staff"),
      row("Pain", doc.pain, "Completed by staff"),
      row("Injuries", doc.injuries_none ? "None observed" : doc.injuries, "Completed by staff"),
      row("Head strike", doc.head_strike, "Completed by staff"),
      row("Post-fall tool", doc.post_fall_instrument, "Completed by staff"),
      row("Post-fall completed", formatWhen(doc.post_fall_completed_at), "Completed by staff"),
      row("Post-fall findings", doc.post_fall_findings, "Completed by staff"),
      row("Distress", doc.distress, "Completed by staff"),
      row("Care given", doc.care_given, "Completed by staff"),
      row("Safety measures", doc.safety_measures, "Completed by staff"),
      row(
        "Hospital",
        doc.hospital_transfer === "yes"
          ? `${doc.hospital_name || ""} ${formatWhen(doc.hospital_time)}`
          : doc.hospital_transfer,
        "Completed by staff"
      )
    ].join("")}</table>
    <h2>3. Your judgement</h2>
    <p>NeuraVue does not decide whether abuse occurred. This answer is the nurse's professional judgement.</p>
    <table>${[
      row("Reasonable grounds", incident.reasonable_grounds_decision.decision, "Completed by staff"),
      row("Basis", incident.reasonable_grounds_decision.suspicion, "Completed by staff"),
      row(
        "Decided by",
        `${incident.reasonable_grounds_decision.decided_by_name || "—"} ${formatWhen(incident.reasonable_grounds_decision.decided_at)}`,
        "Completed by staff"
      ),
      row("Possible criminal offence", doc.criminal_offence, "Completed by staff"),
      row("Category", displayCopy(doc.critical_incident_category), "Completed by staff"),
      row("AH-223-1", doc.qc_ah223_required, "Completed by staff")
    ].join("")}</table>
    <h2>4. Who was told</h2>
    <table>
      <thead><tr><th>Who</th><th>Name</th><th>Time</th><th>How</th><th>Note</th></tr></thead>
      <tbody>
        ${incident.notifications
          .map(
            (item) =>
              `<tr><td>${esc(partyLabel(item.party))}</td><td>${esc(item.person_name)}</td><td>${esc(
                formatWhen(item.at)
              )}</td><td>${esc(item.method)}</td><td>${esc(item.note || item.reference_no)}</td></tr>`
          )
          .join("")}
      </tbody>
    </table>
    <h2>5. Follow-up</h2>
    <table>${[
      row("Immediate prevention", doc.immediate_prevention, "Completed by staff"),
      row("Longer-term actions", doc.long_term_actions, "Completed by staff"),
      row("Plan of care", doc.care_plan_updated, "Completed by staff"),
      row("Current status", doc.outcome, "Completed by staff"),
      row("Investigation lead", doc.investigation_lead, "Completed by staff"),
      row("Official channel recorded by staff", doc.official_channel, "Completed by staff"),
      row("Reference number", doc.official_reference, "Completed by staff")
    ].join("")}</table>
    <h2>Attestation</h2>
    <p>${esc(doc.attestation_name || "—")}, ${esc(doc.attestation_designation || "—")}, login ${esc(
      doc.attestation_user_id || "—"
    )}, ${esc(formatWhen(doc.attestation_at))}.</p>
    <p>I attest this record reflects my own observations and actions and is accurate to the best of my knowledge. I understand NeuraVue does not determine whether abuse occurred and does not submit reports.</p>
    ${
      doc.supervisor_review
        ? `<h2>Manager review</h2><p>${esc(doc.supervisor_review.name)}, ${esc(
            doc.supervisor_review.designation
          )}, ${esc(formatWhen(doc.supervisor_review.at))}. ${esc(
            doc.supervisor_review.note
          )} This is a separate entry, not a co-signature.</p>`
        : ""
    }`
    : waiting
      ? `<p class="banner">Awaiting staff review. This page is the locked alert record and the timeline only. The incident form has not started. Nothing has been submitted.</p>`
      : "";

  const dismissal = incident.false_alert
    ? `<h2>False alert</h2><table>${[
        row("Reason", incident.false_alert.reason_code, "Completed by staff"),
        row("Note", incident.false_alert.note, "Completed by staff"),
        row("Risk found while checking", incident.false_alert.risk_situation, "Completed by staff"),
        row("Concern about care or conduct", incident.false_alert.concern, "Completed by staff"),
        row(
          "Marked by",
          `${incident.false_alert.dismissed_by_name} ${formatWhen(incident.false_alert.dismissed_at)}`,
          "Completed by staff"
        ),
        row(
          "Second person",
          incident.false_alert.confirmation
            ? `${incident.false_alert.confirmation.by_name} ${incident.false_alert.confirmation.decision} ${formatWhen(incident.false_alert.confirmation.at)}`
            : "Waiting",
          "Completed by staff"
        )
      ].join("")}</table>`
    : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>${esc(title)} ${esc(incident.id)}</title>
  <style>
    body { font-family: Georgia, "Times New Roman", serif; color: #1e2229; margin: 32px; line-height: 1.45; }
    h1 { font-size: 22px; margin-bottom: 4px; }
    h2 { font-size: 16px; margin-top: 28px; border-bottom: 1px solid #1e2229; padding-bottom: 4px; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    th, td { border: 1px solid #c5cad3; text-align: left; vertical-align: top; padding: 6px 8px; font-size: 13px; }
    th { width: 28%; background: #f4f5f7; }
    .banner { background: #f4f5f7; border: 1px solid #c5cad3; padding: 12px; }
    .footer { margin-top: 32px; font-size: 12px; }
    .amendment { border-left: 3px solid #1e2229; padding-left: 12px; margin-top: 12px; }
    @media print { body { margin: 12mm; } }
  </style>
</head>
<body>
  <p>EXAMPLE LAYOUT for staff. Not submitted.</p>
  <h1>${esc(title)}</h1>
  <p><strong>${esc(incident.facility_name)}</strong> · ${esc(incident.id)} · ${esc(status)}</p>
  <p>NeuraVue prepared the system facts. Staff review, complete, and submit through the official channel themselves. An alert is not a finding of abuse.</p>
  <h2>Alert and timeline</h2>
  <table>${alertRows}</table>
  <ol>${incident.events.map(eventLine).join("")}</ol>
  ${form}
  ${dismissal}
  <h2>Amendments</h2>
  ${
    incident.amendments.length
      ? incident.amendments.map(amendmentBlock).join("")
      : "<p>No amendments. Original entries stay visible when an amendment is added.</p>"
  }
  <p class="footer">Draft prepared by NeuraVue documentation support. Staff must review, complete, and submit through the official channel. NeuraVue does not submit to a regulator and does not decide whether abuse occurred.</p>
</body>
</html>`;
}

/** Print view for a British Columbia record. Staff copy it. Nothing is submitted. */
export function buildBcPrintHtml(incident: Incident): string {
  const doc = incident.documentation;
  const card = filingCard(incident);
  const suggestion = incident.confirmed_at ? bcCategorySuggest(incident, doc) : null;
  const product =
    incident.type === "unsafe_interaction" || incident.type === "responsive_behaviour"
      ? PRODUCT_BEHAVIOUR
      : typeLabel(incident.type);
  const regulatory = suggestion?.regulatory_term || "Not confirmed";
  const namesOff = incident.site_jurisdiction?.residentNamesAllowed === false;
  const resident = namesOff
    ? [doc.resident_room || incident.room, doc.pcc_patient_id].filter(Boolean).join(" · ")
    : doc.resident_name || incident.room || "—";
  const authority = card.health_authority
    ? `${card.health_authority.name}: ${card.health_authority.channel}`
    : "No health authority is on file for this home.";
  const registry = card.assisted_living_registry
    ? `${card.assisted_living_registry.channel} ${card.assisted_living_registry.form}. ${card.assisted_living_registry.email}. Fax ${card.assisted_living_registry.fax}.`
    : "";
  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>${esc(incident.id)}</title></head><body>
  <p>Prepared for staff to copy into the health authority form or HLTH 1622. Not a verified copy of HLTH 3409 or the Vancouver Coastal Health form. PSLS field names were not verified. Not submitted by NeuraVue.</p>
  <h1>${esc(incident.facility_name)} · ${esc(incident.id)}</h1>
  <p>Resident identifier: ${esc(resident)}</p>
  <p>Category: ${esc(regulatory)} (${esc(product)})</p>
  <p>Additional category: ${doc.restraint_used === "yes" ? "Emergency restraint" : "—"}</p>
  <p>Staff removed from the resident's care: ${esc(doc.staff_removed_from_care || "—")}</p>
  <p>Designated agency: ${esc(doc.aga_reported || "No designated-agency report is recorded.")} ${esc(doc.aga_reference || "")}. The reporter's name is not included.</p>
  <h2>Where to file</h2>
  <p>${esc(authority)}</p>
  ${registry ? `<p>${esc(registry)}</p>` : ""}
  <p>Nothing on this page was sent to a health authority, the Assisted Living Registry, PSLS, or a designated agency.</p>
  <p>Prepared by staff. Fields marked [from alert] were prefilled from a detection and confirmed by the nurse. Not submitted by NeuraVue.</p>
  </body></html>`;
}
